import { IsDateRangeQuery } from '../../core/dates/helpers/date-range-query-helper';
import { ShFacet, ShSelectedFacetValue } from '../../elements/components/facet/entities/facet.entity';

/**
 * A facet is non-discriminating — and therefore not worth rendering — when it offers exactly one option
 * that every object currently in the list already has. Clicking it would return the identical result set.
 *
 * `total` is the size of the population the facet counts were computed over, which is not always the number
 * the page displays. Pass `undefined` when no trustworthy total is available; the rule then never fires.
 */
export function isNonDiscriminating(facet: ShFacet, hasSelected: boolean, total: number | undefined): boolean {
    if (total === undefined) return false;

    // A filter the user is actively filtering on must stay in sight.
    if (hasSelected) return false;

    // A list cut short by the engine's display cap is not a one-option filter.
    if (facet.hasMoreValues) return false;

    if (facet.values.length !== 1) return false;

    // Selecting a date bucket replaces the previous selection, so a date facet is a drill-down rather than
    // a filter: its single bucket may still be the user's only route to narrowing by date.
    if (IsDateRangeQuery(facet.values[0].value)) return false;

    return facet.values[0].count === total;
}

/**
 * Applies {@link isNonDiscriminating} across a panel's facets. Both filter panels are sibling copies, so
 * this is where the rule lives rather than in each of them.
 *
 * Call **upstream of the selected-value filtering**: that strips a facet's selected values out of its list
 * and would leave a multi-valued facet looking like a one-option one.
 */
export function filterDiscriminating(
    facets: ShFacet[],
    selectedFacets: ShSelectedFacetValue[],
    total: number | undefined,
    enabled: boolean
): ShFacet[] {
    if (!enabled) return facets;

    return facets.filter(
        facet =>
            !isNonDiscriminating(
                facet,
                selectedFacets.some(selected => selected.key === facet.key),
                total
            )
    );
}
